import { NgTemplateOutlet } from '@angular/common';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  type ConnectedPosition,
} from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  computed,
  signal,
  viewChild,
  type AfterViewInit,
  type OnInit,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronLeft, lucideSearch } from '@ng-icons/lucide';
import { normalizarTexto } from 'src/app/helpers/busca-eventos.helper';
import {
  OPERADOR_TEXTO,
  interpretarBusca,
  interpretarDia,
  type CampoBusca,
  type FiltrosDinamicos,
} from './filtros-dinamicos';

/** Um filtro aplicado ou removido pela barra (`valor: null` remove). */
export interface FiltroAlterado {
  path: string;
  valor: string | null;
}

/** As duas pontas de um filtro de período; `null` de um lado é intervalo aberto. */
export interface PeriodoAlterado {
  path: string;
  gte: string | null;
  lte: string | null;
}

/** Ids únicos por instância: uma tela renderiza a barra no desktop e no mobile. */
let instanciasBarra = 0;

/**
 * A barra de busca com prefixos — o combobox dos filtros dinâmicos (ADR-0019,
 * GUE-391), compartilhado por todas as telas que leem um catálogo de
 * `/filtros`.
 *
 * Sem prefixo é a busca livre de sempre: cada tecla sai em `buscaChange` e a
 * tela manda como o termo textual que o backend já entendia. Com prefixo
 * (`lote:`, `status:`) vira o filtro daquele campo. Nenhum prefixo é escrito
 * aqui: `camposDeBusca` deriva cada um do rótulo que veio do catálogo, então um
 * filtro novo em `/filtros` ganha prefixo sem tocar em componente nenhum.
 *
 * O que fica de FORA daqui, de propósito: os chips da linha (sessão,
 * ordenação, lista, período) e a exportação. Cada tela tem os seus, e é ali que
 * elas divergem — a barra é a parte que era literalmente igual nas duas.
 *
 * ## Período
 *
 * O formulário de período (dois modos, dois campos) entra por
 * {@link corpoPeriodo}, e não é declarado aqui: no check-in o MESMO formulário
 * também é o corpo do chip de período e do menu "Mais filtros", e um formulário
 * só, declarado na tela, é o que garante que os três caminhos apliquem
 * exatamente o mesmo filtro. Uma tela sem filtro de data não passa nada.
 */
@Component({
  selector: 'app-barra-filtros',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, NgTemplateOutlet, CdkOverlayOrigin, CdkConnectedOverlay],
  providers: [provideIcons({ lucideSearch, lucideChevronLeft })],
  host: { '[class]': 'classeHost' },
  template: `
    <!-- Painel em CdkConnectedOverlay e não num div no fluxo: no desktop a
         toolbar é projetada DENTRO do card da tabela, que tem overflow-hidden —
         um painel posicionado no fluxo seria cortado na borda do card. E não é
         CdkMenu porque o menu rouba o foco do campo: aqui quem navega com as
         setas continua sendo o <input>, via aria-activedescendant. -->
    <div
      class="relative"
      cdkOverlayOrigin
      #origemBarra="cdkOverlayOrigin"
      #caixaBarra
    >
      @if (alta) {
        <img
          src="assets/svg/lupa.svg"
          alt=""
          aria-hidden="true"
          class="pointer-events-none absolute right-[15px] top-1/2 h-6 w-6 -translate-y-1/2 dark:[filter:brightness(0)_invert(1)]"
        />
      } @else {
        <ng-icon
          name="lucideSearch"
          size="16px"
          aria-hidden="true"
          class="pointer-events-none absolute left-2.5 top-[9px] text-muted-foreground"
        />
      }
      <!-- min-h e flex-wrap (e não altura fixa): com pílulas de filtro textual a
           barra cresce em vez de espremer o campo. -->
      <div
        [class]="
          alta
            ? 'flex min-h-12 w-full flex-wrap items-center gap-1 rounded-full border border-border bg-card py-2 pl-[15px] pr-[51px] focus-within:ring-0 dark:border-[#a3a4a8] dark:bg-[#282828]'
            : 'flex min-h-[2rem] w-full flex-wrap items-center gap-1 rounded-input border border-border bg-card py-[3px] pl-8 pr-2 focus-within:ring-2 focus-within:ring-ring'
        "
        (click)="focarBarra()"
      >
        <!-- Pílula só para filtro TEXTUAL: quem tem chip na linha já se mostra
             lá, e o mesmo filtro nos dois lugares são dois estados pra manter
             em sincronia sem ganhar nada. -->
        @for (p of pilulas(); track p.path) {
          <span
            class="inline-flex h-[1.375rem] shrink-0 items-center gap-1 rounded-[7px] bg-accent pl-2 pr-1 text-xs"
          >
            <span class="text-muted-foreground">{{ p.rotulo }}:</span>
            <span class="font-semibold text-foreground">{{ p.valor }}</span>
            <button
              type="button"
              class="inline-flex size-4 items-center justify-center rounded-[5px] text-muted-foreground hover:text-foreground"
              [attr.aria-label]="'Remover filtro ' + p.rotulo"
              (click)="removerTexto(p.path)"
            >
              &times;
            </button>
          </span>
        }
        <input
          #campoBarra
          type="search"
          role="combobox"
          autocomplete="off"
          [class]="
            alta
              ? 'h-[1.5rem] min-w-[7rem] flex-1 border-0 bg-transparent p-0 text-[0.85rem] text-[#09244b] placeholder:text-[#999999] focus-visible:outline-none dark:text-[#fafafa] dark:placeholder:text-[#d9d9d9]'
              : 'h-[1.5rem] min-w-[7rem] flex-1 border-0 bg-transparent p-0 text-sm text-foreground focus-visible:outline-none'
          "
          [placeholder]="placeholder"
          [attr.aria-label]="rotuloAcessivel"
          [attr.aria-expanded]="painelAberto()"
          [attr.aria-controls]="idPainel"
          [attr.aria-activedescendant]="idItemDestacado()"
          [value]="texto()"
          (input)="aoDigitarNaBarra($any($event.target).value)"
          (keydown)="aoTeclarNaBarra($event)"
          (focus)="aoFocarBarra()"
        />
      </div>

      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="origemBarra"
        [cdkConnectedOverlayOpen]="painelAberto()"
        [cdkConnectedOverlayPositions]="posicoesBarra"
        [cdkConnectedOverlayViewportMargin]="12"
        (overlayOutsideClick)="aoClicarFora($event)"
        (detach)="barraAberta.set(false)"
      >
        <!-- admin-portal-scope repetido: o CDK portaliza pra fora do DOM do
             shell e sem a classe o painel perde os tokens do anel. -->
        <div
          [id]="idPainel"
          class="admin-portal-scope flex w-[min(30rem,92vw)] flex-col overflow-hidden rounded-card border border-border bg-popover text-popover-foreground shadow-float"
        >
          @if (alvo(); as a) {
            <div
              class="flex items-center gap-2 border-b border-border px-2.5 py-2"
            >
              <button
                type="button"
                class="inline-flex size-5 items-center justify-center rounded-[6px] text-muted-foreground hover:text-foreground"
                aria-label="Voltar à lista de filtros"
                (mousedown)="$event.preventDefault()"
                (click)="voltarAosCampos()"
              >
                <ng-icon name="lucideChevronLeft" size="14px" />
              </button>
              <span
                class="inline-flex h-5 shrink-0 items-center rounded-[6px] border border-border bg-muted px-1.5 text-xs font-semibold text-foreground"
                >{{ a.campo.prefixo }}:</span
              >
              <span class="text-sm font-semibold">{{ a.campo.rotulo }}</span>
              @if (a.campo.tipo === 'lista') {
                <span class="ml-auto text-xs text-muted-foreground"
                  >{{ opcoesAlvo().length }} de
                  {{ a.campo.opcoes.length }}</span
                >
              }
            </div>

            @switch (a.campo.tipo) {
              @case ('lista') {
                <div
                  class="max-h-72 overflow-y-auto p-1.5"
                  role="listbox"
                  [attr.aria-label]="a.campo.rotulo"
                >
                  @for (o of opcoesAlvo(); track o.valor; let i = $index) {
                    <button
                      type="button"
                      role="option"
                      [id]="idPainel + '-' + i"
                      [attr.aria-selected]="i === destaque()"
                      [class]="classeItem(i === destaque())"
                      (mousedown)="$event.preventDefault()"
                      (click)="aplicarValor(a.campo, o.valor)"
                    >
                      <span class="flex-1 truncate text-left">{{
                        o.rotulo
                      }}</span>
                    </button>
                  } @empty {
                    <!-- Este @empty FICA (ao contrário do da lista de campos):
                         a pessoa escolheu "status:" de propósito, e sumir com o
                         painel esconderia em que campo ela está. -->
                    <p class="m-0 px-2 py-2 text-sm text-muted-foreground">
                      Nada com «{{ a.termo }}».
                    </p>
                  }
                </div>
              }
              @case ('periodo') {
                <!-- Um dia completo digitado depois do prefixo ('data:26/08/2026')
                     aplica direto: é a seleção única, que o par De/Até só
                     expressa repetindo a data nos dois campos. Enquanto não for
                     um dia válido, o formulário de intervalo continua sendo o
                     caminho. -->
                @if (diaDigitado(); as dia) {
                  <button
                    type="button"
                    [class]="classeItem(true) + ' m-1.5'"
                    (mousedown)="$event.preventDefault()"
                    (click)="aplicarDia(a.campo, dia)"
                  >
                    <span class="flex-1 truncate text-left"
                      >Filtrar {{ a.campo.rotulo.toLowerCase() }} pelo dia
                      {{ dia }}</span
                    >
                    <span
                      class="inline-flex h-5 shrink-0 items-center rounded-[6px] border border-border px-1.5 text-[11px] text-muted-foreground"
                      >&#9166;</span
                    >
                  </button>
                } @else if (corpoPeriodo; as corpo) {
                  <div class="p-2.5">
                    <ng-container
                      [ngTemplateOutlet]="corpo"
                      [ngTemplateOutletContext]="{ $implicit: a.campo }"
                    />
                  </div>
                }
              }
              @default {
                <!-- Textual: sem popup de valor nenhum. O que se digita depois
                     dos dois-pontos JÁ é o valor; o painel só confirma o que o
                     Enter vai fazer. -->
                @if (a.termo) {
                  <button
                    type="button"
                    [class]="classeItem(true) + ' m-1.5'"
                    (mousedown)="$event.preventDefault()"
                    (click)="aplicarValor(a.campo, a.termo)"
                  >
                    <span class="flex-1 truncate text-left"
                      >Filtrar {{ a.campo.rotulo.toLowerCase() }} por «{{
                        a.termo
                      }}»</span
                    >
                    <span
                      class="inline-flex h-5 shrink-0 items-center rounded-[6px] border border-border px-1.5 text-[11px] text-muted-foreground"
                      >&#9166;</span
                    >
                  </button>
                } @else {
                  <p class="m-0 px-3 py-2.5 text-sm text-muted-foreground">
                    Digite o texto para filtrar por
                    {{ a.campo.rotulo.toLowerCase() }}.
                  </p>
                }
              }
            }
          } @else {
            <!-- Sem @empty aqui de propósito: quando nenhum campo casa o painel
                 não abre (ver painelTemConteudo), então a lista vazia é
                 inalcançável. Um aviso de "nada casou" tapava a lista de
                 resultados justamente quando ela é o que interessa. -->
            <div
              class="max-h-72 overflow-y-auto p-1.5"
              role="listbox"
              aria-label="Filtros disponíveis"
            >
              <p
                class="m-0 px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Filtrar por
              </p>
              @for (c of camposSugeridos(); track c.path; let i = $index) {
                <button
                  type="button"
                  role="option"
                  [id]="idPainel + '-' + i"
                  [attr.aria-selected]="i === destaque()"
                  [class]="classeItem(i === destaque())"
                  (mousedown)="$event.preventDefault()"
                  (click)="escolherCampo(c)"
                >
                  <span
                    class="inline-flex h-5 shrink-0 items-center rounded-[6px] border border-border bg-muted px-1.5 text-xs font-semibold text-foreground"
                    >{{ c.prefixo }}:</span
                  >
                  <span class="flex-1 truncate text-left">{{ c.rotulo }}</span>
                  <span class="shrink-0 text-xs text-muted-foreground">{{
                    dicaCampo(c)
                  }}</span>
                </button>
              }
            </div>
          }

          @if (mostrarDicasDeTeclado()) {
            <div
              data-dicas-teclado
              class="flex flex-wrap items-center gap-3 border-t border-border bg-muted px-2.5 py-1.5 text-[11px] text-muted-foreground"
            >
              <span>&#8593;&#8595; navegar</span>
              <span>&#9166; selecionar</span>
              <span>&#9003; apagar o último</span>
              <span>esc fechar</span>
            </div>
          }
        </div>
      </ng-template>
    </div>
  `,
})
export class BarraFiltrosComponent implements OnInit, AfterViewInit {
  /**
   * ## Por que `@Input()`/`@Output()` e não `input()`/`output()`
   *
   * Este é o primeiro componente do repo que existe para ser usado DENTRO de
   * outro, e o vitest daqui compila os specs sem o plugin Angular: em JIT o
   * compilador nunca registra `input()`/`output()` no def do componente, então
   * `<app-barra-filtros [campos]="...">` num template pai é NG0303 — a
   * ligação é descartada e o filho fica no default, calado. Medido com uma
   * sonda de duas linhas: `@Input()` chega, `input()` não; `@Output()` chega,
   * `output()` não. Ver `src/app/testing/definir-input.ts`, que documenta o
   * mesmo buraco pelo lado do `setInput`.
   *
   * Os decorators guardam em signal para que os `computed()` daqui continuem
   * funcionando iguais. Quando o projeto ganhar o transform Angular no vitest,
   * isto pode voltar a ser `input()`/`output()` — e os specs de quem usa a
   * barra param de depender deste detalhe.
   */
  private readonly _busca = signal('');
  /** Termo que veio de fora (deeplink, estado restaurado) — semeia o campo. */
  @Input() set busca(valor: string) {
    this._busca.set(valor ?? '');
  }

  private readonly _campos = signal<CampoBusca[]>([]);
  /**
   * O catálogo na forma que a barra usa (`camposDeBusca`): inclui os filtros
   * textuais, que não viram chip, e traz o prefixo de cada campo. Vazio = a
   * barra se comporta exatamente como o campo de busca antigo.
   */
  @Input() set campos(valor: CampoBusca[]) {
    this._campos.set(valor ?? []);
  }

  private readonly _filtros = signal<FiltrosDinamicos>({});
  /** Filtros aplicados agora, chaveados por `path` — ver `FiltrosDinamicos`. */
  @Input() set filtros(valor: FiltrosDinamicos) {
    this._filtros.set(valor ?? {});
  }

  @Input() placeholder = 'Buscar';
  @Input() rotuloAcessivel = 'Buscar ou digitar um filtro';
  /**
   * Sem o teto de 560px, a barra cresce quatro vezes um irmão `flex-1`. Meus
   * eventos usa isso para a busca ocupar mais da linha que ordem e status.
   * O check-in fica no teto: lá a barra convive com chips que não devem
   * esticar.
   */
  @Input() semTeto = false;
  /** 48px em vez de 32px. Meus eventos: os três campos da linha ficam da mesma altura. */
  @Input() alta = false;

  /** Classe do host. Um valor só: `max-w-[560px]` e `flex-[4]` não coexistem. */
  get classeHost(): string {
    return this.semTeto
      ? 'block min-w-52 flex-[4]'
      : 'block min-w-[200px] flex-1 max-w-[560px]';
  }
  /** Formulário de período da tela; ausente = a tela não tem filtro de data. */
  @Input() corpoPeriodo: TemplateRef<unknown> | null = null;

  /** Cada tecla, sem debounce: quem controla o disparo é a tela. */
  @Output() readonly buscaChange = new EventEmitter<string>();
  @Output() readonly listaChange = new EventEmitter<FiltroAlterado>();
  /** Filtro textual aplicado pela barra (`nome:maria`); `valor: null` remove. */
  @Output() readonly textoChange = new EventEmitter<FiltroAlterado>();
  @Output() readonly periodoChange = new EventEmitter<PeriodoAlterado>();

  /** Ids do painel/opções para o `aria-activedescendant`, únicos por instância. */
  readonly idPainel = `barra-filtros-${++instanciasBarra}`;

  /**
   * O que está ESCRITO na barra agora — inclui a expressão em construção
   * (`lote:cam`), que não é busca livre. O que sai em `buscaChange` é só o
   * texto sem prefixo reconhecido (ver {@link aoDigitarNaBarra}).
   */
  readonly texto = signal('');
  readonly barraAberta = signal(false);
  readonly destaque = signal(0);

  readonly posicoesBarra: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -6,
    },
  ];

  /** Foco ao abrir Meus eventos não deve abrir o painel de filtros. */
  private focoAutomatico = false;

  private readonly campoBarra =
    viewChild<ElementRef<HTMLInputElement>>('campoBarra');
  private readonly caixaBarra =
    viewChild<ElementRef<HTMLElement>>('caixaBarra');

  ngOnInit(): void {
    this.texto.set(this._busca());
  }

  ngAfterViewInit(): void {
    if (!this.alta) return;
    this.focoAutomatico = true;
    this.campoBarra()?.nativeElement.focus({ preventScroll: true });
    this.focoAutomatico = false;
  }

  aoFocarBarra(): void {
    if (this.focoAutomatico) return;
    this.barraAberta.set(true);
  }

  /** `lote:cam` → o campo Lote com o termo "cam"; texto solto → `null`. */
  readonly alvo = computed(() =>
    interpretarBusca(this.texto(), this._campos()),
  );

  /** No modo raiz a lista é filtrada pelo que já foi digitado — `lo` acha Lote. */
  readonly camposSugeridos = computed(() => {
    const termo = normalizarTexto(this.texto()).trim();
    if (!termo) return this._campos();
    return this._campos().filter(
      (c) =>
        normalizarTexto(c.rotulo).includes(termo) ||
        c.prefixo.startsWith(termo),
    );
  });

  readonly opcoesAlvo = computed(() => {
    const alvo = this.alvo();
    if (!alvo || alvo.campo.tipo !== 'lista') return [];
    const termo = normalizarTexto(alvo.termo);
    if (!termo) return alvo.campo.opcoes;
    return alvo.campo.opcoes.filter((o) =>
      normalizarTexto(o.rotulo).includes(termo),
    );
  });

  /** O que as setas percorrem: campos no modo raiz, opções dentro de uma lista. */
  private readonly itensNavegaveis = computed(() => {
    const alvo = this.alvo();
    if (!alvo) return this.camposSugeridos().length;
    return alvo.campo.tipo === 'lista' ? this.opcoesAlvo().length : 0;
  });

  /**
   * Um painel que só sabe dizer "nada casou" não é resposta: quem digitou
   * "festival" está buscando evento, não filtro, e o painel taparia a lista
   * justamente na hora em que ela é o que interessa. Dentro de um campo a
   * regra se inverte — ali o painel FICA, porque o campo escolhido é a
   * informação.
   */
  readonly painelTemConteudo = computed(
    () => !!this.alvo() || this.camposSugeridos().length > 0,
  );

  readonly painelAberto = computed(
    () => this.barraAberta() && this.painelTemConteudo(),
  );

  /**
   * Setas, Enter e Esc só existem para quem tem teclado. Detecção por
   * capacidade, e não por user agent: o que decide é haver ponteiro fino e
   * hover, que é a mesma pergunta que se faz do aparelho.
   */
  readonly mostrarDicasDeTeclado = computed(() => {
    // jsdom não implementa matchMedia. Na dúvida as dicas aparecem: mostrá-las
    // a quem não as usa é ruído; escondê-las de quem as usa esconde a função.
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    )
      return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  });

  readonly idItemDestacado = computed(() =>
    this.painelAberto() && this.itensNavegaveis() > 0
      ? `${this.idPainel}-${this.destaque()}`
      : null,
  );

  /**
   * Pílulas na barra só para filtro TEXTUAL: lista e período já se mostram no
   * próprio chip da tela, e repetir o valor nos dois lugares é dois estados
   * pra manter em sincronia sem ganhar nada.
   */
  readonly pilulas = computed(() =>
    this._campos()
      .filter((c) => c.tipo === 'texto')
      .map((c) => ({
        path: c.path,
        rotulo: c.rotulo,
        valor: this._filtros()[c.path]?.[OPERADOR_TEXTO] ?? '',
      }))
      .filter((p) => !!p.valor),
  );

  /** O dia completo escrito depois de `data:`, se já for um; `null` enquanto se digita. */
  readonly diaDigitado = computed(() => {
    const alvo = this.alvo();
    return alvo?.campo.tipo === 'periodo' ? interpretarDia(alvo.termo) : null;
  });

  /**
   * Vai como `gte`+`lte` iguais, não como `eq`: quem colapsa o par num dia é a
   * tela, e é bom que a regra viva num lugar só — o chip, digitando a mesma
   * data nos dois campos, cai exatamente no mesmo caminho.
   */
  aplicarDia(campo: CampoBusca, dia: string): void {
    this.periodoChange.emit({ path: campo.path, gte: dia, lte: dia });
    this.limparCampo();
  }

  dicaCampo(campo: CampoBusca): string {
    if (campo.tipo === 'lista') return `${campo.opcoes.length} opções`;
    return campo.tipo === 'periodo' ? 'dia ou faixa' : 'texto';
  }

  classeItem(destacado: boolean): string {
    const base =
      'flex h-8 w-full cursor-pointer items-center gap-2 rounded-[8px] px-2 text-sm text-foreground ';
    return destacado
      ? base + 'bg-accent'
      : base + 'bg-transparent hover:bg-accent';
  }

  focarBarra(): void {
    this.campoBarra()?.nativeElement.focus();
  }

  aoDigitarNaBarra(valor: string): void {
    this.texto.set(valor);
    this.destaque.set(0);
    this.barraAberta.set(true);
    // Expressão com prefixo NÃO é busca livre: mandar `lote:cam` como termo
    // esvaziaria a lista enquanto a pessoa ainda está escolhendo o valor.
    this.buscaChange.emit(this.alvo() ? '' : valor);
  }

  aoTeclarNaBarra(evento: KeyboardEvent): void {
    const ultimo = Math.max(this.itensNavegaveis() - 1, 0);
    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault();
        this.barraAberta.set(true);
        this.destaque.update((i) => Math.min(i + 1, ultimo));
        break;
      case 'ArrowUp':
        evento.preventDefault();
        this.destaque.update((i) => Math.max(i - 1, 0));
        break;
      case 'Escape':
        this.barraAberta.set(false);
        break;
      case 'Enter':
        evento.preventDefault();
        this.confirmarBarra();
        break;
      case 'Backspace':
        // Só com o campo vazio: apagar letra continua apagando letra.
        if (!this.texto()) {
          const ultima = this.pilulas().at(-1);
          if (ultima) this.removerTexto(ultima.path);
        }
        break;
    }
  }

  private confirmarBarra(): void {
    const alvo = this.alvo();
    if (!alvo) {
      const campo = this.camposSugeridos()[this.destaque()];
      if (campo) this.escolherCampo(campo);
      return;
    }
    if (alvo.campo.tipo === 'lista') {
      const opcao = this.opcoesAlvo()[this.destaque()];
      if (opcao) this.aplicarValor(alvo.campo, opcao.valor);
      return;
    }
    const dia = this.diaDigitado();
    if (dia) {
      this.aplicarDia(alvo.campo, dia);
      return;
    }
    // Sem dia digitado, período aplica pelos próprios <input type="date">.
    if (alvo.campo.tipo === 'texto' && alvo.termo)
      this.aplicarValor(alvo.campo, alvo.termo);
  }

  /** `lo` + Enter completa para `lote:` — não é preciso digitar o prefixo inteiro. */
  escolherCampo(campo: CampoBusca): void {
    this.texto.set(`${campo.prefixo}:`);
    this.destaque.set(0);
    this.barraAberta.set(true);
    this.buscaChange.emit('');
    this.focarBarra();
  }

  aplicarValor(campo: CampoBusca, valor: string): void {
    if (campo.tipo === 'lista')
      this.listaChange.emit({ path: campo.path, valor });
    else this.textoChange.emit({ path: campo.path, valor });
    this.limparCampo();
  }

  removerTexto(path: string): void {
    this.textoChange.emit({ path, valor: null });
  }

  voltarAosCampos(): void {
    this.texto.set('');
    this.destaque.set(0);
    this.buscaChange.emit('');
    this.focarBarra();
  }

  /**
   * Esvazia a barra SEM emitir nada — para a tela chamar junto do seu "Limpar
   * filtros". A barra é dona do próprio texto (só é semeada pelo `busca` no
   * ngOnInit), então limpar pelo lado da tela não a esvaziaria sozinho e
   * ficaria um termo escrito sobre uma lista que não está mais filtrada por
   * ele. Sem emissão porque quem chama já está avisando a lista.
   */
  limparTexto(): void {
    this.texto.set('');
    this.destaque.set(0);
  }

  /** Campo esvazia e o painel volta pro menu: pronto pro filtro seguinte. */
  private limparCampo(): void {
    this.texto.set('');
    this.destaque.set(0);
    this.buscaChange.emit('');
    this.focarBarra();
  }

  aoClicarFora(evento: MouseEvent): void {
    const alvo = evento.target as Node | null;
    if (alvo && this.caixaBarra()?.nativeElement.contains(alvo)) return;
    this.barraAberta.set(false);
  }
}
